import React, {useCallback, useEffect, useState} from 'react';
import {formatDate as formatApiDate} from '../../utils/dateTime';
import Spinner from '../../components/Spinner';
import ConfirmDialog from '../../components/ConfirmDialog';
import Dialog from '../../components/Dialog';
import {
    ApiTokenCreated,
    ApiTokenInfo,
    createApiToken,
    fetchApiTokens,
    PUBLIC_API_SCOPES,
    revokeApiToken,
} from '../../service/ApiTokenService';

const EXPIRY_OPTIONS: Array<{ value: string; label: string; days: number | null }> = [
    {value: '30', label: '30 дней', days: 30},
    {value: '90', label: '90 дней', days: 90},
    {value: '365', label: '1 год', days: 365},
    {value: 'never', label: 'Бессрочно', days: null},
];

const STATUS_META: Record<ApiTokenInfo['status'], { label: string; className: string }> = {
    active: {label: 'Активен', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'},
    expired: {label: 'Истёк', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'},
    revoked: {label: 'Отозван', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'},
};

/** ISO LocalDateTime (без зоны) через days дней — формат, который принимает backend. */
export function expiresAtFromDays(days: number | null, now: Date = new Date()): string | null {
    if (days === null) return null;
    const date = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
        + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

const formatDate = (value: string | null): string => formatApiDate(value);

interface CreateModalProps {
    onClose: () => void;
    onCreated: (created: ApiTokenCreated) => void;
}

const CreateKeyModal: React.FC<CreateModalProps> = ({onClose, onCreated}) => {
    const [name, setName] = useState('');
    const [scopes, setScopes] = useState<Set<string>>(new Set());
    const [expiry, setExpiry] = useState('90');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleScope = (scope: string) => {
        setScopes(prev => {
            const next = new Set(prev);
            if (next.has(scope)) next.delete(scope); else next.add(scope);
            return next;
        });
    };

    const valid = name.trim().length >= 3 && name.trim().length <= 100 && scopes.size > 0;

    const handleCreate = async () => {
        if (!valid) return;
        setSaving(true);
        setError(null);
        try {
            const days = EXPIRY_OPTIONS.find(o => o.value === expiry)?.days ?? null;
            const created = await createApiToken({
                name: name.trim(),
                scopes: Array.from(scopes),
                expiresAt: expiresAtFromDays(days),
            });
            onCreated(created);
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const description = (e as any)?.response?.data?.description || (e as any)?.response?.data?.message;
            setError(description || 'Не удалось создать ключ. Попробуйте ещё раз.');
            setSaving(false);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            title="Создать ключ API"
            busy={saving}
            data-testid="create-api-key-modal"
            panelClassName="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[85vh] flex flex-col outline-none"
            bodyClassName="flex-1 overflow-y-auto p-4 space-y-4"
            footer={
                <>
                    <button className="btn-secondary" onClick={onClose} disabled={saving}>Отмена</button>
                    <button className="btn-primary" onClick={handleCreate} disabled={!valid || saving}>
                        {saving ? 'Создаём…' : 'Создать'}
                    </button>
                </>
            }
        >
                    <div>
                        <label htmlFor="api-key-name"
                               className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Название
                        </label>
                        <input
                            id="api-key-name"
                            type="text"
                            value={name}
                            maxLength={100}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Например: мой ИИ-ассистент"
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">От 3 до 100 символов.</p>
                    </div>

                    <fieldset>
                        <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Разрешения (скоупы)
                        </legend>
                        <div className="space-y-2">
                            {PUBLIC_API_SCOPES.map(({scope, label, description}) => (
                                <label key={scope}
                                       className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-600 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <input
                                        type="checkbox"
                                        checked={scopes.has(scope)}
                                        onChange={() => toggleScope(scope)}
                                        className="mt-0.5"
                                    />
                                    <span>
                                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {label} <code className="text-xs text-gray-500 dark:text-gray-400">{scope}</code>
                                        </span>
                                        <span className="block text-xs text-gray-500 dark:text-gray-400">{description}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <div>
                        <label htmlFor="api-key-expiry"
                               className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Срок действия
                        </label>
                        <select
                            id="api-key-expiry"
                            value={expiry}
                            onChange={(e) => setExpiry(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                        >
                            {EXPIRY_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    {error && (
                        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
                            {error}
                        </div>
                    )}
        </Dialog>
    );
};

const SecretModal: React.FC<{ created: ApiTokenCreated; onClose: () => void }> = ({created, onClose}) => {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(created.token);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            title={`Ключ «${created.tokenInfo.name}» создан`}
            showClose={false}
            data-testid="api-key-secret-modal"
            panelClassName="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full outline-none"
            headerClassName="p-4 border-b border-gray-200 dark:border-gray-600"
            bodyClassName="p-4 space-y-3"
            footerClassName="flex justify-end p-4 border-t border-gray-200 dark:border-gray-600"
            footer={<button className="btn-primary" onClick={onClose}>Готово</button>}
        >
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
                        Скопируйте ключ сейчас — он показывается <strong>только один раз</strong> и
                        не может быть восстановлен.
                    </div>
                    <code
                        data-testid="api-key-secret"
                        className="block w-full break-all rounded-lg bg-gray-100 dark:bg-gray-900 p-3 text-xs text-gray-900 dark:text-gray-100"
                    >
                        {created.token}
                    </code>
                    <button className="btn-secondary w-full" onClick={copy}>
                        {copied ? 'Скопировано ✓' : 'Скопировать в буфер обмена'}
                    </button>
        </Dialog>
    );
};

const ProfileApiKeysPage: React.FC = () => {
    const [tokens, setTokens] = useState<ApiTokenInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [createdKey, setCreatedKey] = useState<ApiTokenCreated | null>(null);
    const [revokeTarget, setRevokeTarget] = useState<ApiTokenInfo | null>(null);
    const [revoking, setRevoking] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            setTokens(await fetchApiTokens());
        } catch (e) {
            console.error('Error fetching API tokens:', e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreated = (created: ApiTokenCreated) => {
        setShowCreate(false);
        setCreatedKey(created);
        load();
    };

    const handleRevoke = async () => {
        if (!revokeTarget) return;
        setRevoking(true);
        try {
            await revokeApiToken(revokeTarget.id);
            setRevokeTarget(null);
            await load();
        } catch (e) {
            console.error('Error revoking API token:', e);
        } finally {
            setRevoking(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="card p-6">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Ключи API</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-xl">
                            Ключи дают персональным ИИ-ассистентам доступ к вашему гардеробу через
                            публичный API (<code>/api/public/v1</code>) — строго в пределах выбранных
                            разрешений. Спецификация API:{' '}
                            <a className="text-primary-600 dark:text-primary-400 hover:underline"
                               href="/api/public/v1/openapi.yaml" target="_blank" rel="noopener noreferrer">
                                openapi.yaml
                            </a>.
                        </p>
                    </div>
                    <button className="btn-primary" onClick={() => setShowCreate(true)}>Создать ключ</button>
                </div>

                {loading && (
                    <div className="flex justify-center py-10"><Spinner/></div>
                )}

                {!loading && error && (
                    <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300 flex items-center justify-between gap-3">
                        <span>Не удалось загрузить ключи.</span>
                        <button className="btn-secondary" onClick={load}>Повторить</button>
                    </div>
                )}

                {!loading && !error && tokens.length === 0 && (
                    <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                        У вас пока нет ключей API. Создайте первый, чтобы подключить ассистента.
                    </div>
                )}

                {!loading && !error && tokens.length > 0 && (
                    <div className="overflow-x-auto -mx-6 px-6">
                        <table className="min-w-full text-sm">
                            <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                <th className="py-2 pr-4 font-medium">Название</th>
                                <th className="py-2 pr-4 font-medium">Префикс</th>
                                <th className="py-2 pr-4 font-medium">Скоупы</th>
                                <th className="py-2 pr-4 font-medium">Создан</th>
                                <th className="py-2 pr-4 font-medium">Истекает</th>
                                <th className="py-2 pr-4 font-medium">Использован</th>
                                <th className="py-2 pr-4 font-medium">Статус</th>
                                <th className="py-2 font-medium"><span className="sr-only">Действия</span></th>
                            </tr>
                            </thead>
                            <tbody>
                            {tokens.map(token => (
                                <tr key={token.id} className="border-b border-gray-100 dark:border-gray-700/50">
                                    <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">{token.name}</td>
                                    <td className="py-3 pr-4">
                                        <code className="text-xs text-gray-600 dark:text-gray-300">clths_{token.prefix}…</code>
                                    </td>
                                    <td className="py-3 pr-4">
                                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                                            {token.scopes.map(scope => (
                                                <span key={scope}
                                                      className="rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-300">
                                                    {scope}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{formatDate(token.createdAt)}</td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{token.expiresAt ? formatDate(token.expiresAt) : 'Бессрочно'}</td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{formatDate(token.lastUsedAt)}</td>
                                    <td className="py-3 pr-4">
                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[token.status]?.className ?? STATUS_META.revoked.className}`}>
                                            {STATUS_META[token.status]?.label ?? token.status}
                                        </span>
                                    </td>
                                    <td className="py-3 text-right">
                                        {token.status === 'active' && (
                                            <button
                                                className="text-sm text-red-600 dark:text-red-400 hover:underline"
                                                onClick={() => setRevokeTarget(token)}
                                            >
                                                Отозвать
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showCreate && (
                <CreateKeyModal onClose={() => setShowCreate(false)} onCreated={handleCreated}/>
            )}

            {createdKey && (
                <SecretModal created={createdKey} onClose={() => setCreatedKey(null)}/>
            )}

            <ConfirmDialog
                open={revokeTarget !== null}
                title="Отозвать ключ?"
                description={revokeTarget
                    ? `Ключ «${revokeTarget.name}» немедленно перестанет действовать. Отзыв необратим.`
                    : null}
                confirmLabel={revoking ? 'Отзываем…' : 'Отозвать'}
                destructive
                busy={revoking}
                onConfirm={handleRevoke}
                onCancel={() => setRevokeTarget(null)}
                data-testid="revoke-api-key-dialog"
            />
        </div>
    );
};

export default ProfileApiKeysPage;
